import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Save, Plus, Trash2, X, Upload, FileText, ToggleRight, ToggleLeft } from 'lucide-react';

const KINDS = [
  { id: 'must', label: 'Must' },
  { id: 'must_not', label: 'Must Not' },
  { id: 'tone', label: 'Tone' },
  { id: 'scope', label: 'Scope' },
];

export default function BrainPanel() {
  const [settings, setSettings] = useState(null);
  const [rules, setRules] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [savingS, setSavingS] = useState(false);
  const [savingR, setSavingR] = useState(false);
  const [newRule, setNewRule] = useState({ text: '', kind: 'must', priority: 5, active: true });
  const [newTopic, setNewTopic] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadErr, setUploadErr] = useState(null);

  const load = () => {
    Promise.all([
      api.get('/api/admin/brain/settings'),
      api.get('/api/admin/brain/rules'),
      api.get('/api/admin/ee/training-materials'),
    ]).then(([s, r, m]) => { setSettings(s.data); setRules(r.data || []); setMaterials(m.data || []); }).catch(() => {});
  };
  useEffect(load, []);

  const updateField = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const saveSettings = async () => {
    setSavingS(true);
    try { await api.put('/api/admin/brain/settings', settings); }
    finally { setSavingS(false); }
  };
  const addTopic = () => {
    const t = newTopic.trim(); if (!t) return;
    setSettings((s) => ({ ...s, off_limits_topics: [...(s.off_limits_topics || []), t] }));
    setNewTopic('');
  };
  const removeTopic = (t) => setSettings((s) => ({ ...s, off_limits_topics: s.off_limits_topics.filter((x) => x !== t) }));

  const addRule = async () => {
    if (!newRule.text.trim()) return;
    setSavingR(true);
    try {
      await api.post('/api/admin/brain/rules', newRule);
      setNewRule({ text: '', kind: 'must', priority: 5, active: true });
      load();
    } finally { setSavingR(false); }
  };
  const updateRule = async (id, patch) => {
    await api.put(`/api/admin/brain/rules/${id}`, patch);
    load();
  };
  const deleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    await api.delete(`/api/admin/brain/rules/${id}`);
    load();
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploadBusy(true); setUploadErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name);
      await api.post('/api/admin/brain/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch (e) { setUploadErr(e.message); }
    finally { setUploadBusy(false); e.target.value = ''; }
  };
  const toggleMaterial = async (id) => { await api.put(`/api/admin/ee/training-materials/${id}/toggle`); load(); };
  const deleteMaterial = async (id) => { if (!window.confirm('Delete this material?')) return; await api.delete(`/api/admin/ee/training-materials/${id}`); load(); };

  if (!settings) return <div className="text-ee-muted">Loading…</div>;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* PERSONA */}
      <div className="ee-card p-6">
        <h2 className="text-white font-serif text-xl mb-1">Persona &amp; Voice</h2>
        <p className="text-xs text-ee-muted mb-4">How EE introduces itself and responds.</p>
        <div className="space-y-3">
          {[ ['persona_name','Name'], ['voice_description','Voice'], ['response_style','Response Style'], ['intro_message','Intro Message'], ['refusal_message','Refusal Message'], ['additional_instructions','Additional Instructions'] ].map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-xs uppercase tracking-wider text-gold/80">{label}</span>
              <textarea data-testid={`brain-${k}`} value={settings[k] || ''} onChange={(e) => updateField(k, e.target.value)} rows={k === 'voice_description' || k === 'intro_message' || k === 'refusal_message' || k === 'additional_instructions' ? 2 : 1} className="mt-1 w-full bg-ee-bg border border-ee-border rounded-md p-2 text-white text-sm focus:border-gold outline-none" />
            </label>
          ))}
          <div className="grid grid-cols-2 gap-2">
            <label className="block"><span className="text-xs uppercase tracking-wider text-gold/80">Max Length</span>
              <select value={settings.max_response_length || 'medium'} onChange={(e) => updateField('max_response_length', e.target.value)} className="mt-1 w-full bg-ee-bg border border-ee-border rounded-md p-2 text-white text-sm">
                <option value="short">Short</option><option value="medium">Medium</option><option value="long">Long</option>
              </select>
            </label>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gold/80 mb-1">Off-Limits Topics</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(settings.off_limits_topics || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-500/10 border border-red-500/30 text-red-300 rounded-full text-xs">{t}<button onClick={() => removeTopic(t)} className="hover:text-white"><X size={10} /></button></span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={newTopic} onChange={(e) => setNewTopic(e.target.value)} placeholder="Add a topic" onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTopic())} className="flex-1 bg-ee-bg border border-ee-border rounded-md p-2 text-sm text-white" />
              <button onClick={addTopic} className="text-xs px-3 rounded border border-gold/40 text-gold hover:bg-gold/10">Add</button>
            </div>
          </div>
          <button data-testid="save-persona" onClick={saveSettings} disabled={savingS} className="w-full inline-flex items-center justify-center gap-1 py-2 rounded-md gold-grad text-black font-semibold text-sm disabled:opacity-50"><Save size={14} /> {savingS ? 'Saving…' : 'Save Persona Settings'}</button>
        </div>
      </div>

      {/* RULES */}
      <div className="ee-card p-6">
        <h2 className="text-white font-serif text-xl mb-1">Behavior Rules</h2>
        <p className="text-xs text-ee-muted mb-4">Higher priority rules win conflicts. Active rules are baked into EE’s system prompt.</p>
        <div className="space-y-2 mb-4 max-h-[420px] overflow-y-auto pr-1">
          {rules.length === 0 && <div className="text-sm text-ee-muted">No rules yet.</div>}
          {rules.map((r) => (
            <div key={r.rule_id} className="bg-ee-bg border border-ee-border rounded-md p-3">
              <div className="flex items-start gap-2">
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${r.kind === 'must_not' ? 'bg-red-500/10 text-red-300 border-red-500/30' : r.kind === 'must' ? 'bg-green-500/10 text-green-300 border-green-500/30' : 'bg-blue-500/10 text-blue-300 border-blue-500/30'}`}>{r.kind}</span>
                <span className="text-xs text-ee-muted">P{r.priority}</span>
                <textarea defaultValue={r.text} onBlur={(e) => e.target.value !== r.text && updateRule(r.rule_id, { text: e.target.value })} rows={2} className="flex-1 bg-transparent text-sm text-white outline-none resize-none" />
                <button onClick={() => updateRule(r.rule_id, { active: !r.active })} className="text-ee-muted hover:text-gold" title={r.active ? 'Active' : 'Disabled'}>{r.active ? <ToggleRight className="text-green-400" size={18} /> : <ToggleLeft size={18} />}</button>
                <button onClick={() => deleteRule(r.rule_id)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-ee-border pt-4">
          <div className="text-xs uppercase tracking-wider text-gold/80 mb-2">Add New Rule</div>
          <div className="space-y-2">
            <textarea data-testid="new-rule-text" value={newRule.text} onChange={(e) => setNewRule({ ...newRule, text: e.target.value })} placeholder="e.g. Never give medical advice. Always redirect to a professional." rows={2} className="w-full bg-ee-bg border border-ee-border rounded-md p-2 text-sm text-white" />
            <div className="flex items-center gap-2">
              <select value={newRule.kind} onChange={(e) => setNewRule({ ...newRule, kind: e.target.value })} className="bg-ee-bg border border-ee-border rounded-md px-2 py-1.5 text-sm text-white">{KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}</select>
              <label className="flex items-center gap-1 text-xs text-ee-muted">Priority<input type="number" min={1} max={10} value={newRule.priority} onChange={(e) => setNewRule({ ...newRule, priority: parseInt(e.target.value, 10) || 1 })} className="w-14 bg-ee-bg border border-ee-border rounded-md px-2 py-1 text-white" /></label>
              <button data-testid="add-rule" onClick={addRule} disabled={savingR} className="ml-auto px-3 py-1.5 rounded gold-grad text-black font-semibold text-sm inline-flex items-center gap-1"><Plus size={12} /> Add</button>
            </div>
          </div>
        </div>
      </div>

      {/* KNOWLEDGE BASE */}
      <div className="ee-card p-6 lg:col-span-2">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-white font-serif text-xl">Knowledge Base</h2>
          <label className="inline-flex items-center gap-1 px-3 py-1.5 rounded gold-grad text-black font-semibold text-sm cursor-pointer">
            <Upload size={14} /> {uploadBusy ? 'Uploading…' : 'Upload document'}
            <input type="file" accept=".txt,.md,.pdf,.docx" onChange={uploadFile} className="hidden" disabled={uploadBusy} />
          </label>
        </div>
        <p className="text-xs text-ee-muted mb-4">Active documents are appended to EE’s system prompt. Up to 10MB each.</p>
        {uploadErr && <div className="mb-3 text-sm text-red-400 bg-red-400/10 border border-red-400/30 rounded-md p-2">{uploadErr}</div>}
        <div className="divide-y divide-ee-border">
          {materials.length === 0 && <div className="text-sm text-ee-muted py-4">No knowledge documents yet.</div>}
          {materials.map((m) => (
            <div key={m.material_id} className="py-3 flex items-center gap-3">
              <FileText size={16} className="text-gold flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-white text-sm truncate">{m.title || m.filename}</div>
                <div className="text-xs text-ee-muted">{m.material_type || 'document'} · {m.size_bytes ? `${Math.round(m.size_bytes / 1024)}KB` : ''}</div>
              </div>
              <button onClick={() => toggleMaterial(m.material_id)} className="text-ee-muted hover:text-gold" title={m.active ? 'Active' : 'Inactive'}>{m.active ? <ToggleRight className="text-green-400" size={20} /> : <ToggleLeft size={20} />}</button>
              <button onClick={() => deleteMaterial(m.material_id)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
